// B-109 — the pure half of Reportes' "Tendencias de la conciliación": what coordination reads over a
// month rather than a day. Four groupings, each from data the backend already answers:
//   - the month's cash differences by office and by region — the amount every alert of the month
//     carries (`listCierreAlerts`' `amount`, from the client's `alert_priority:`), summed as absolute
//     values so a sobrante and a faltante do not cancel;
//   - the offices with the most differences raised in the month (alerts of severity alert/subalert:
//     a value still missing — `blocked` — or good news — `info` — is not a difference);
//   - the open pending notes by age and by kind (`listCierrePendingItems`);
//   - the billed TX by concept, month by month (`getCierreBillingSummary`, one month per call).
// Names no client: offices, regions and concepts come labelled from the backend. A date is
// `YYYY-MM-DD` / a month `YYYY-MM`, never a `Date` in the browser's zone.

import { ANTIGUEDADES, diasAbierta, tipoDePendiente } from "./cierreCuadreDia";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** `2026-10` → "oct 2026". */
export function etiquetaDeMes(mes) {
  const [y, m] = String(mes || "").split("-").map(Number);
  return y && m ? `${MESES[m - 1]} ${y}` : String(mes || "");
}

/** The month's first and last day, the last one cut at `hoy` for the month still running. */
export function rangoDelMes(mes, hoy) {
  const [y, m] = mes.split("-").map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const fin = `${mes}-${String(ultimo).padStart(2, "0")}`;
  return { desde: `${mes}-01`, hasta: hoy && hoy < fin && hoy >= `${mes}-01` ? hoy : fin };
}

/** The `n` months up to and including `mes`, oldest first. */
export function mesesHasta(mes, n = 6) {
  const [y, m] = mes.split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const total = y * 12 + (m - 1) - (n - 1 - i);
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
  });
}

const numero = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const esDiferencia = (a) => a && (a.severity === "alert" || a.severity === "subalert");

/** The month's differences with an amount, one per office: `{ codigo, oficina, region, total, veces }`, largest first. */
export function diferenciasPorOficina(alertas, n = 10) {
  const porOficina = new Map();
  (alertas || []).forEach((a) => {
    const monto = numero(a.amount);
    if (monto === null || !esDiferencia(a)) return;
    const codigo = String(a.officeCode || "—");
    const actual = porOficina.get(codigo) || {
      codigo, oficina: a.officeName || codigo, region: a.region || "Sin región", total: 0, veces: 0,
    };
    actual.total += Math.abs(monto);
    actual.veces += 1;
    porOficina.set(codigo, actual);
  });
  return [...porOficina.values()].sort((x, y) => y.total - x.total).slice(0, n);
}

/** The same, by region: `{ region, total, veces, oficinas }`, largest first. */
export function diferenciasPorRegion(alertas) {
  const porRegion = new Map();
  (alertas || []).forEach((a) => {
    const monto = numero(a.amount);
    if (monto === null || !esDiferencia(a)) return;
    const region = a.region || "Sin región";
    const actual = porRegion.get(region) || { region, total: 0, veces: 0, oficinas: new Set() };
    actual.total += Math.abs(monto);
    actual.veces += 1;
    actual.oficinas.add(String(a.officeCode));
    porRegion.set(region, actual);
  });
  return [...porRegion.values()]
    .map((r) => ({ ...r, oficinas: r.oficinas.size }))
    .sort((x, y) => y.total - x.total);
}

/** The offices with the most differences raised in the month: `{ codigo, oficina, region, alertas, porTipo }`. */
export function oficinasConMasAlertas(alertas, n = 10) {
  const porOficina = new Map();
  (alertas || []).filter(esDiferencia).forEach((a) => {
    const codigo = String(a.officeCode || "—");
    const actual = porOficina.get(codigo) || {
      codigo, oficina: a.officeName || codigo, region: a.region || "Sin región", alertas: 0, porTipo: {},
    };
    actual.alertas += 1;
    actual.porTipo[a.ruleKey] = (actual.porTipo[a.ruleKey] || 0) + 1;
    porOficina.set(codigo, actual);
  });
  return [...porOficina.values()]
    .sort((x, y) => y.alertas - x.alertas || x.codigo.localeCompare(y.codigo, undefined, { numeric: true }))
    .slice(0, n);
}

/** The open notes by age band (ANTIGUEDADES, without "any") and by kind, with their totals. */
export function pendientesPorAntiguedad(items, hoy) {
  const abiertas = (items || []).filter((i) => i.status === "OPEN");
  const bandas = ANTIGUEDADES.filter((b) => b.key).map((b) => ({ ...b, cantidad: 0, monto: 0 }));
  const porTipo = new Map();
  let monto = 0;
  abiertas.forEach((i) => {
    const dias = diasAbierta(i, hoy);
    const valor = numero(i.amount) || 0;
    monto += valor;
    const banda = bandas.find((b) => dias !== null && (b.min === undefined || dias >= b.min) && (b.max === undefined || dias <= b.max));
    if (banda) {
      banda.cantidad += 1;
      banda.monto += valor;
    }
    const tipo = porTipo.get(i.kind) || { tipo: i.kind, etiqueta: tipoDePendiente(i.kind), cantidad: 0, monto: 0 };
    tipo.cantidad += 1;
    tipo.monto += valor;
    porTipo.set(i.kind, tipo);
  });
  const viejas = abiertas
    .map((i) => ({ ...i, dias: diasAbierta(i, hoy) }))
    .sort((x, y) => (y.dias ?? -1) - (x.dias ?? -1))
    .slice(0, 5);
  return {
    cantidad: abiertas.length,
    monto,
    bandas: bandas.map(({ key, label, cantidad, monto: m }) => ({ key, etiqueta: label, cantidad, monto: m })),
    porTipo: [...porTipo.values()].sort((x, y) => y.cantidad - x.cantidad),
    masViejas: viejas,
  };
}

/**
 * The billed TX by concept, one point per month: `{ puntos: [{ mes, etiqueta, [conceptKey]: tx }],
 * conceptos: [{ key, label }] }`. `meses` is `[{ mes, datos }]`, `datos` a billing answer or null
 * (a month that could not be read is a point with no bars, never zeros).
 */
export function txPorConceptoYMes(meses) {
  const conceptos = new Map();
  const puntos = (meses || []).map(({ mes, datos }) => {
    const punto = { mes, etiqueta: etiquetaDeMes(mes), total: datos ? 0 : null };
    if (!datos) return punto;
    (datos.concepts || []).forEach((c) => conceptos.set(c.key, c.label));
    (datos.rows || []).forEach((r) => {
      const tx = numero(r.tx) || 0;
      punto[r.conceptKey] = (punto[r.conceptKey] || 0) + tx;
      punto.total += tx;
    });
    return punto;
  });
  return { puntos, conceptos: [...conceptos].map(([key, label]) => ({ key, label })) };
}

/**
 * One month of `txPorConceptoYMes`, as a ranking: `{ mes, etiqueta, total, anterior, variacion,
 * conceptos: [{ key, label, tx, share, anterior, delta }] }`, most TX first, concepts with no TX in
 * the month left out. `anterior` is the month before it in the series (null when there is none or it
 * could not be read), `variacion` the change in percent (null without a base). Null for a month the
 * series does not hold or could not read.
 */
export function conceptosDelMes(tx, mes) {
  const puntos = tx?.puntos || [];
  const i = puntos.findIndex((p) => p.mes === mes);
  const punto = puntos[i];
  if (!punto || punto.total === null) return null;
  const previo = i > 0 && puntos[i - 1].total !== null ? puntos[i - 1] : null;
  const conceptos = (tx.conceptos || [])
    .map((c) => {
      const valor = punto[c.key] || 0;
      const antes = previo ? previo[c.key] || 0 : null;
      return {
        key: c.key,
        label: c.label,
        tx: valor,
        share: punto.total ? Math.round((valor / punto.total) * 1000) / 10 : 0,
        anterior: antes,
        delta: antes === null ? null : valor - antes,
      };
    })
    .filter((c) => c.tx > 0)
    .sort((a, b) => b.tx - a.tx);
  const anterior = previo ? previo.total : null;
  return {
    mes: punto.mes,
    etiqueta: punto.etiqueta,
    total: punto.total,
    anterior,
    variacion: anterior ? Math.round(((punto.total - anterior) / anterior) * 1000) / 10 : null,
    conceptos,
  };
}
